
// src/components/comunicacion/AvisosUrgentes.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../../../api";
import { getSessionUserId, isSessionSuperAdmin, getSessionEmpresaId } from "../../../../utils/SessionUser";
import { useLogos } from "../../../../context/LogoProvider";
import { ENDPOINTS } from "../../Utils/Endpoints";
import AlertDialog from "../../../Lms/Components/Shared/AlertDialog";
import ConfirmDialog from "../../../Lms/Components/Shared/ConfirmDialog";
import sigmaDelete from '../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../Pages/img/Editar.png';
import ConfirmDeleteDialog from "../../../security/ConfirmDeleteDialog";

const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
const UPLOAD_ENDPOINT = `${API_BASE}/upload/aviso`;

const getTodayISO = () => {
    return new Date().toISOString().slice(0, 10);
};



const AvisosUrgentes = ({ onNavigate, initialItem }) => {
    const { getSystemConfig } = useLogos();
    const [avisos, setAvisos] = useState([]);
    const [filtros, setFiltros] = useState({
        texto: "",
        estado: "todos",
        urgencia: "todos",
        idAreaNegocio: "todos"
    });
    const [editingId, setEditingId] = useState(null);
    // ... (state setup)

    // ... (formData def)
    const [formData, setFormData] = useState(() => ({
        titulo: "",
        descripcion: "",
        tipoAviso: "Baja", // Alta, Media, Baja
        fechaInicio: getTodayISO(),
        fechaFin: getTodayISO(),
        idAreaNegocio: "",
        activo: true,
        imagen: null,
    }));
    const [errores, setErrores] = useState({});
    const [areasNegocio, setAreasNegocio] = useState([]);

    // Custom Dialogs State
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info', onClose: () => { } });
    const showAlertDialog = (title, message, variant = 'info') => {
        setAlertDialog({ open: true, title, message, variant, onClose: () => setAlertDialog(prev => ({ ...prev, open: false })) });
    };

    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', variant: 'danger', onConfirm: () => { }, onClose: () => { } });
    const showConfirmDialog = (title, message, onConfirm, variant = 'danger') => {
        setConfirmDialog({ open: true, title, message, variant, onConfirm, onClose: () => setConfirmDialog(prev => ({ ...prev, open: false })) });
    };

    // Estados para manejo de imágenes
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // Estados para el modal
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
    const [isUploading, setIsUploading] = useState(false);
    const [previousImagePath, setPreviousImagePath] = useState(null);

    const [showShareModal, setShowShareModal] = useState(false);
    const [descendantCompanies, setDescendantCompanies] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState({});
    const [sharingLoading, setSharingLoading] = useState(false);

    const getImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return `${SERVER_URL}${path}`;
    };


    // Cargar avisos desde API
    const cargarAvisos = async () => {
        try {
            const response = await axios.get(ENDPOINTS.avisos); // Usar axios.get
            const data = response.data; // Axios devuelve los datos en .data
            const transformados = data.map(a => ({
                id: a.idAviso ?? a.IdAviso,
                titulo: a.titulo ?? a.Titulo,
                descripcion: a.mensaje ?? a.Mensaje ?? "",
                tipoAviso: a.tipoAviso ?? a.TipoAviso ?? "Baja",
                fechaInicio: (a.fechaInicio ?? a.FechaInicio ?? "").slice(0, 10),
                fechaFin: (a.fechaFin ?? a.FechaFin ?? "").slice(0, 10),
                idAreaNegocio: a.idAreaNegocio ?? a.IdAreaNegocio,
                activo: a.mostrarEnDashboard ?? a.MostrarEnDashboard,
                imagen: a.imagenUrl ?? a.ImagenUrl ?? null,
                esOrigen: a.esOrigen ?? a.EsOrigen ?? false,
                idEmpresa: a.idEmpresa ?? a.IdEmpresa,
                ubicacion: 'todas'
            }));

            // Ordenar: Activos primero, luego por fecha inicio desc, luego por ID desc
            transformados.sort((a, b) => {
                if (a.activo && !b.activo) return -1;
                if (!a.activo && b.activo) return 1;

                const dateA = new Date(a.fechaInicio || 0).getTime();
                const dateB = new Date(b.fechaInicio || 0).getTime();
                if (dateB !== dateA) return dateB - dateA;

                return b.id - a.id;
            });

            setAvisos(transformados);
        } catch (error) {
            console.error("Error al cargar avisos:", error);
        }
    };

    useEffect(() => {
        cargarAvisos();

        // Cargar áreas
        const cargarAreas = async () => {
            try {
                const r = await axios.get(ENDPOINTS.areasNegocios);
                const rawAreas = r.data || [];
                
                const isSuper = isSessionSuperAdmin();
                const myEmpresaId = getSessionEmpresaId();

                const filteredAreas = rawAreas.filter(a => {
                    if (isSuper) return true;
                    const empId = a.idEmpresa || a.IdEmpresa;
                    return empId === myEmpresaId || !empId || empId === 0;
                });

                setAreasNegocio(filteredAreas);
            } catch (e) { console.error(e); }
        };
        cargarAreas();
    }, []);

    // Handle deep linking / initial item selection
    useEffect(() => {
        if (initialItem && avisos.length > 0 && !editingId) {
            const found = avisos.find(a => a.id === initialItem.id);
            if (found) {
                // We reuse handleEditar logic but need to be careful not to create loop if handleEditar was simpler
                // Ideally we just call handleEditar
                handleEditar(found);
                // Scroll to form?
                const formElement = document.querySelector('.intra-avisos-form-card');
                if (formElement) formElement.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [initialItem, avisos]);

    const handleFiltrosChange = (e) => {
        const { name, value } = e.target;
        setFiltros((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const avisosFiltrados = useMemo(() => {
        return avisos.filter((aviso) => {
            // Filtro por texto (título o descripción)
            if (filtros.texto) {
                const textoLower = filtros.texto.toLowerCase();
                if (
                    !aviso.titulo.toLowerCase().includes(textoLower) &&
                    !aviso.descripcion.toLowerCase().includes(textoLower)
                ) {
                    return false;
                }
            }

            // Filtro por estado
            if (filtros.estado !== "todos") {
                const esActivo = filtros.estado === "activo";
                if (aviso.activo !== esActivo) {
                    return false;
                }
            }

            // Filtro por urgencia
            if (filtros.urgencia !== "todos") {
                if (aviso.tipoAviso !== filtros.urgencia) {
                    return false;
                }
            }

            // Filtro por área de negocio
            if (filtros.idAreaNegocio !== "todos") {
                if (aviso.idAreaNegocio?.toString() !== filtros.idAreaNegocio.toString()) {
                    return false;
                }
            }

            return true;
        });
    }, [avisos, filtros]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const validarFormulario = () => {
        const nuevoErrores = {};
        if (!formData.titulo.trim()) nuevoErrores.titulo = "El título es obligatorio.";
        if (!formData.descripcion.trim()) nuevoErrores.descripcion = "La descripción es obligatoria.";
        if (!formData.tipoAviso) nuevoErrores.tipoAviso = "El nivel de urgencia es obligatorio.";
        if (!formData.idAreaNegocio) nuevoErrores.idAreaNegocio = "El área es obligatoria.";
        if (!formData.fechaFin) nuevoErrores.fechaFin = "Fecha fin obligatoria.";

        // Validar que la fecha de fin no sea anterior a hoy
        const hoy = getTodayISO();
        if (formData.fechaFin && formData.fechaFin < hoy) {
            nuevoErrores.fechaFin = "La fecha fin no puede ser anterior a hoy.";
        }

        // Imagen ya no es obligatoria
        /* if (!imagePreview) {
            nuevoErrores.imagen = "La imagen es obligatoria.";
        } */

        setErrores(nuevoErrores);
        return Object.keys(nuevoErrores).length === 0;
    };

    const limpiarFormulario = () => {
        setFormData({
            titulo: "",
            descripcion: "",
            tipoAviso: "Baja",
            fechaInicio: getTodayISO(),
            fechaFin: getTodayISO(),
            idAreaNegocio: (areasNegocio.length > 0 ? areasNegocio[0].idAreaNegocio : ""),
            activo: true,
            imagen: null,
        });
        setEditingId(null);
        setErrores({});
        setSelectedFile(null);
        setImagePreview(null);
        setPreviousImagePath(null);
    };

    const handleNuevoAviso = () => {
        limpiarFormulario();
        setModalMode('create');
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validarFormulario()) return;

        try {
            setIsUploading(true);
            const sessionEmpresaId = getSessionEmpresaId();
            
            // Obtener empresas descendientes
            const resDesc = await axios.get(ENDPOINTS.empresasDescendientes(sessionEmpresaId));
            const descendientes = resDesc.data || [];
            
            if (descendientes.length === 0) {
                await executePublish({});
                return;
            }

            let preSelected = {};
            if (editingId) {
                try {
                    const resShared = await axios.get(ENDPOINTS.avisosCompartidosByAviso(editingId));
                    const sharedData = resShared.data || [];
                    sharedData.forEach(sh => {
                        if (!sh.esOrigen && !sh.EsOrigen) {
                            const empId = sh.IdEmpresa || sh.idEmpresa;
                            preSelected[empId] = true;
                        }
                    });
                } catch(e) { console.warn("Could not fetch shared", e); }
            }

            setDescendantCompanies(descendientes);
            setSelectedCompanies(preSelected);
            setShowModal(false);
            setShowShareModal(true);
        } catch (error) {
            console.error('Error fetching descendants:', error);
            showAlertDialog("Error", "No se pudo verificar las empresas disponibles para compartir.", "error");
        } finally {
            setIsUploading(false);
        }
    };

    const executePublish = async (selectedShareCompanies = {}) => {
        setIsUploading(true);
        setSharingLoading(true);
        const idUsuario = getSessionUserId();

        try {
            let finalImageUrl = formData.imagen;

            // 1. Manejo de borrado/subida en S3
            if (selectedFile) {
                // Borrar imagen anterior si existía una en S3
                if (previousImagePath && previousImagePath.startsWith('http')) {
                    try {
                        await axios.delete(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(previousImagePath)}`);
                    } catch (err) {
                        console.warn("No se pudo eliminar la imagen anterior de S3:", err);
                    }
                }

                // Subir nueva imagen
                const formDataImg = new FormData();
                formDataImg.append('file', selectedFile);
                const resImg = await axios.post(UPLOAD_ENDPOINT, formDataImg, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                finalImageUrl = resImg.data.filePath;
            } else if (!imagePreview && previousImagePath) {
                // Caso: se eliminó la imagen manualmente
                if (previousImagePath.startsWith('http')) {
                    try {
                        await axios.delete(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(previousImagePath)}`);
                    } catch (err) {
                        console.warn("No se pudo eliminar la imagen de S3:", err);
                    }
                }
                finalImageUrl = "";
            }

            const payload = {
                idAviso: editingId || 0,
                titulo: formData.titulo,
                mensaje: formData.descripcion,
                tipoAviso: formData.tipoAviso,
                fechaInicio: formData.fechaInicio,
                fechaFin: formData.fechaFin,
                idAreaNegocio: parseInt(formData.idAreaNegocio),
                mostrarEnDashboard: formData.activo,
                idUsuarioCreador: idUsuario,
                fechaCreacion: new Date().toISOString(),
                fechaModificacion: new Date().toISOString(),
                idUsuarioModificador: idUsuario,
                imagenUrl: finalImageUrl || "",
                idEmpresa: getSessionEmpresaId()
            };

            let currentAvisoId = editingId;

            if (editingId) {
                await axios.put(ENDPOINTS.avisos, payload);
            } else {
                const resPost = await axios.post(ENDPOINTS.avisos, payload);
                currentAvisoId = resPost.data?.nuevoId ?? resPost.data?.NuevoId ?? resPost.data;
            }

            if (currentAvisoId) {
                try {
                    const resShared = await axios.get(ENDPOINTS.avisosCompartidosByAviso(currentAvisoId));
                    const existingShared = resShared.data || [];
                    
                    for (const idEmpStr of Object.keys(selectedShareCompanies)) {
                        const empId = Number(idEmpStr);
                        const isSelected = selectedShareCompanies[empId];
                        const existingRec = existingShared.find(s => (s.idEmpresa || s.IdEmpresa) === empId);

                        if (isSelected && !existingRec) {
                            await axios.post(ENDPOINTS.avisosCompartidos, {
                                idAviso: currentAvisoId,
                                idEmpresa: empId,
                                activo: true
                            });
                        } else if (!isSelected && existingRec) {
                            await axios.delete(`${ENDPOINTS.avisosCompartidos}/${existingRec.idAvisoCompartido || existingRec.IdAvisoCompartido}`);
                        }
                    }
                } catch(shareErr) {
                    console.error("Error updating sharing permissions", shareErr);
                }
            }

            if (editingId) {
                showAlertDialog("Aviso actualizado", "El aviso se actualizó correctamente.", "success");
            } else {
                showAlertDialog("Aviso creado", "El aviso se creó correctamente.", "success");
            }

            setShowModal(false);
            setShowShareModal(false);
            limpiarFormulario();
            cargarAvisos();
        } catch (error) {
            console.error("Error submit:", error);
            const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            showAlertDialog("Error", `Error al guardar: ${errorMessage}`, "error");
        } finally {
            setIsUploading(false);
            setSharingLoading(false);
        }
    };

    const handleEditar = (aviso) => {
        setEditingId(aviso.id);
        setFormData({
            titulo: aviso.titulo,
            descripcion: aviso.descripcion,
            tipoAviso: aviso.tipoAviso || "Baja",
            fechaInicio: aviso.fechaInicio,
            fechaFin: aviso.fechaFin,
            idAreaNegocio: aviso.idAreaNegocio || (areasNegocio.length > 0 ? areasNegocio[0].idAreaNegocio : ""),
            activo: aviso.activo,
            imagen: aviso.imagen || null,
        });

        // Setear preview de imagen con soporte S3/Local
        setImagePreview(getImageUrl(aviso.imagen));
        setPreviousImagePath(aviso.imagen);
        setSelectedFile(null);
        setErrores({});

        setModalMode('edit');
        setShowModal(true);
    };

    const executeDelete = async () => {
        const item = deleteDialog.item;
        if (!item || !item.id) return;

        try {
            // 1. Borrar imagen de S3 si aplica
            if (item.imagen && item.imagen.startsWith('http')) {
                try {
                    await axios.delete(`${UPLOAD_ENDPOINT}?filePath=${encodeURIComponent(item.imagen)}`);
                } catch (err) {
                    console.warn("No se pudo eliminar la imagen de S3 al borrar el aviso:", err);
                }
            }

            // 2. Borrar registro de DB
            await axios.delete(`${ENDPOINTS.avisos}/${item.id}`);
            await cargarAvisos();
            if (editingId === item.id) limpiarFormulario();
            showAlertDialog("Aviso eliminado", "El aviso se eliminó correctamente.", "success");
            setDeleteDialog({ open: false, item: null });
        } catch (error) {
            console.error("Error al eliminar:", error);
            const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            showAlertDialog("Error al eliminar", errorMessage, "error");
        }
    };

    const handleEliminar = (avisoToDelete) => {
        setDeleteDialog({ open: true, item: avisoToDelete });
    };

    const handleToggleActivo = async (aviso) => {
        const nuevoEstado = !aviso.activo;

        const payload = {
            idAviso: aviso.id,
            titulo: aviso.titulo,
            mensaje: aviso.descripcion,
            tipoAviso: aviso.tipoAviso,
            fechaInicio: aviso.fechaInicio,
            fechaFin: aviso.fechaFin,
            idAreaNegocio: aviso.idAreaNegocio,
            mostrarEnDashboard: nuevoEstado,
            idUsuarioCreador: getSessionUserId(),
            fechaCreacion: new Date().toISOString(),
            fechaModificacion: new Date().toISOString(),
            idUsuarioModificador: getSessionUserId(),
            imagenUrl: aviso.imagen || "",
            idEmpresa: aviso.idEmpresa
        };

        try {
            await axios.put(ENDPOINTS.avisos, payload); // Usar axios.put
            await cargarAvisos(); // Refetch from API
            showAlertDialog("Estado actualizado", `Aviso ${nuevoEstado ? 'activado' : 'desactivado'}.`, "success");
        } catch (error) {
            console.error(error);
            const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            showAlertDialog("Error", `Error al cambiar estado: ${errorMessage}`, "error");
        }
    };

    // UI Helpers (mantenidos)
    const getEtiquetaUbicacion = (u) => 'Todas las páginas';
    const estaVigente = (a) => {
        const hoy = getTodayISO();
        return a.fechaInicio <= hoy && a.fechaFin >= hoy;
    };

    // Aviso Principal Preview
    const avisoPrincipal = useMemo(() => {
        const activos = avisos.filter(a => a.activo && estaVigente(a));
        if (!activos.length) return null;
        // Solo tomar el primero activo vigente
        return activos[0];
    }, [avisos]);

    return (
        <div className="intra-avisos-page">
            <header className="intra-avisos-header">
                <div>
                    {onNavigate && (
                        <button
                            type="button"
                            className="intra-button-ghost btn-cancel"
                            onClick={() => onNavigate("dashboard")}
                            style={{
                                marginBottom: '0.75rem'
                            }}
                        >
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>←</span>
                            <span>Volver al dashboard</span>
                        </button>
                    )}
                    <h1 className="intra-avisos-title">Gestor de avisos urgentes</h1>
                </div>
            </header>



            {/* FILTROS */}
            <section className="intra-news-toolbar">
                <div className="intra-news-filters" style={{ width: '100%', display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                    <div className="intra-filter-group" style={{ flex: '1', minWidth: '200px' }}>
                        <label className="intra-label" htmlFor="filtroTexto">
                            Buscar
                        </label>
                        <input
                            id="filtroTexto"
                            name="texto"
                            type="text"
                            className="intra-input"
                            value={filtros.texto}
                            onChange={handleFiltrosChange}
                            placeholder="Buscar avisos..."
                        />
                    </div>



                    <div className="intra-filter-group" style={{ width: '150px' }}>
                        <label className="intra-label" htmlFor="filtroEstado">
                            Estado
                        </label>
                        <select
                            id="filtroEstado"
                            name="estado"
                            className="intra-select"
                            value={filtros.estado}
                            onChange={handleFiltrosChange}
                        >
                            <option value="todos">Todos</option>
                            <option value="activo">Activo</option>
                            <option value="inactivo">Inactivo</option>
                        </select>
                    </div>

                    <div className="intra-filter-group" style={{ width: '150px' }}>
                        <label className="intra-label" htmlFor="filtroUrgencia">
                            Prioridad
                        </label>
                        <select
                            id="filtroUrgencia"
                            name="urgencia"
                            className="intra-select"
                            value={filtros.urgencia}
                            onChange={handleFiltrosChange}
                        >
                            <option value="todos">Todas</option>
                            <option value="Alta">Alta</option>
                            <option value="Media">Media</option>
                            <option value="Baja">Baja</option>
                        </select>
                    </div>

                    <div className="intra-filter-group" style={{ width: '180px' }}>
                        <label className="intra-label" htmlFor="filtroArea">
                            Área de Negocio
                        </label>
                        <select
                            id="filtroArea"
                            name="idAreaNegocio"
                            className="intra-select"
                            value={filtros.idAreaNegocio}
                            onChange={handleFiltrosChange}
                        >
                            <option value="todos">Todas</option>
                            {areasNegocio.map((a, idx) => (
                                <option key={a.idAreaNegocio || a.IdAreaNegocio || idx} value={a.idAreaNegocio || a.IdAreaNegocio}>
                                    {a.nombre || a.Nombre}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="button"
                        className="btn-action"
                        onClick={handleNuevoAviso}
                        style={{
                            alignSelf: 'flex-end',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        + Nuevo aviso
                    </button>
                </div>
            </section>

            {/* LAYOUT PRINCIPAL - LISTA COMPLETA */}
            <section className="intra-news-layout" style={{ display: 'block' }}>
                <div className="intra-news-list-panel" style={{ width: '100%' }}>
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Listado de avisos</h2>
                        <p className="intra-news-count">
                            {avisosFiltrados.length} aviso{avisosFiltrados.length === 1 ? "" : "s"} encontrado{avisosFiltrados.length === 1 ? "" : "s"}
                        </p>
                    </div>
                    {avisosFiltrados.length === 0 ? (
                        <p className="intra-avisos-empty">No se encontraron avisos con los filtros seleccionados.</p>
                    ) : (
                        <div className="intra-avisos-table-wrapper">
                            <table className="intra-avisos-table">
                                <thead>
                                    <tr>
                                        <th>Imagen</th>
                                        <th>Título y Urgencia</th>
                                        <th>Área de Negocio</th>
                                        <th>Vigencia</th>
                                        <th>Estado</th>
                                        <th>Editar</th>
                                        <th>Eliminar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {avisosFiltrados.map((aviso, idx) => (
                                        <tr key={aviso.id ?? idx}>
                                            <td style={{ width: '80px', padding: '0.5rem' }}>
                                                {aviso.imagen ? (
                                                    <img
                                                        src={getImageUrl(aviso.imagen)}
                                                        alt={aviso.titulo}
                                                        style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            objectFit: 'cover',
                                                            borderRadius: '6px',
                                                            border: '1px solid #e2e8f0'
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        backgroundColor: '#f1f5f9',
                                                        borderRadius: '6px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#cbd5e1',
                                                        fontSize: '1.5rem'
                                                    }}>
                                                        🖼️
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="intra-avisos-title-cell">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                                        <span className="intra-avisos-title-main">{aviso.titulo}</span>
                                                        <span style={{
                                                            fontSize: '0.7rem',
                                                            fontWeight: '700',
                                                            textTransform: 'uppercase',
                                                            padding: '2px 8px',
                                                            borderRadius: '10px',
                                                            backgroundColor: aviso.tipoAviso === 'Alta' ? '#FEE2E2' : aviso.tipoAviso === 'Media' ? '#FEF3C7' : '#E0F2FE',
                                                            color: aviso.tipoAviso === 'Alta' ? '#B91C1C' : aviso.tipoAviso === 'Media' ? '#B45309' : '#0369A1',
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                        }}>
                                                            {aviso.tipoAviso}
                                                        </span>
                                                    </div>
                                                    <span className="intra-avisos-title-sub">{(aviso.descripcion || '').slice(0, 50)}...</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="intra-avisos-meta">
                                                    {areasNegocio.find(a => (a.idAreaNegocio || a.IdAreaNegocio) == aviso.idAreaNegocio)?.nombre 
                                                     || areasNegocio.find(a => (a.idAreaNegocio || a.IdAreaNegocio) == aviso.idAreaNegocio)?.Nombre 
                                                     || "Sin área"}
                                                </span>
                                            </td>
                                            <td><span className="intra-avisos-meta">{aviso.fechaInicio} → {aviso.fechaFin}</span></td>
                                            <td>
                                                <div
                                                    onClick={() => handleToggleActivo(aviso)}
                                                    style={{
                                                        width: '40px',
                                                        height: '24px',
                                                        backgroundColor: aviso.activo ? '#10b981' : '#cbd5e1',
                                                        borderRadius: '12px',
                                                        position: 'relative',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    title={aviso.activo ? "Desactivar" : "Activar"}
                                                >
                                                    <div style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        backgroundColor: 'white',
                                                        borderRadius: '50%',
                                                        position: 'absolute',
                                                        top: '2px',
                                                        left: aviso.activo ? '18px' : '2px',
                                                        transition: 'left 0.2s',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                    }} />
                                                </div>
                                            </td>
                                            <td>
                                                {(aviso.esOrigen || isSessionSuperAdmin()) && (
                                                    <button
                                                        type="button"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                        onClick={() => handleEditar(aviso)}
                                                        title="Editar aviso"
                                                    >
                                                        <img src={sigmaEdit} alt="Editar" style={{ width: '40px', height: 'auto' }} />
                                                    </button>
                                                )}
                                            </td>
                                            <td>
                                                {(aviso.esOrigen || isSessionSuperAdmin()) && (
                                                    <button
                                                        type="button"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                        onClick={() => handleEliminar(aviso)}
                                                        title="Eliminar aviso"
                                                    >
                                                        <img src={sigmaDelete} alt="Eliminar" style={{ width: '40px', height: 'auto' }} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </section>

            {/* MODAL */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }} onClick={() => setShowModal(false)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        width: '90%',
                        maxWidth: '800px',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }} onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="intra-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0' }}>
                            <h2 className="intra-panel-title" style={{ margin: 0, fontSize: '1.25rem' }}>
                                {modalMode === 'create' ? "Nuevo aviso urgente" : "Detalle"}
                            </h2>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '2rem',
                                        lineHeight: 1,
                                        color: '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Modal Content - Scrollable */}
                        <div style={{ padding: '2rem', overflowY: 'auto' }}>
                            <form className="intra-avisos-form" onSubmit={handleSubmit}>
                                <div className="intra-form-group">
                                    <label className="intra-label">Título</label>
                                    <input
                                        name="titulo"
                                        type="text"
                                        className={`intra-input ${errores.titulo ? "intra-input-error" : ""}`}
                                        value={formData.titulo}
                                        onChange={handleChange}
                                        placeholder="Ej. Corte de energía..."
                                    />
                                    {errores.titulo && <p className="intra-error-text">{errores.titulo}</p>}
                                </div>

                                <div className="intra-form-row">
                                    <div className="intra-form-group">
                                        <label className="intra-label">Nivel de Urgencia</label>
                                        <select
                                            name="tipoAviso"
                                            className={`intra-select ${errores.tipoAviso ? "intra-input-error" : ""}`}
                                            value={formData.tipoAviso}
                                            onChange={handleChange}
                                        >
                                            <option value="Baja">Baja</option>
                                            <option value="Media">Media</option>
                                            <option value="Alta">Alta</option>
                                        </select>
                                        {errores.tipoAviso && <p className="intra-error-text">{errores.tipoAviso}</p>}
                                    </div>
                                    <div className="intra-form-group">
                                        <label className="intra-label">Área de Negocio</label>
                                        <select
                                            name="idAreaNegocio"
                                            className={`intra-select ${errores.idAreaNegocio ? "intra-input-error" : ""}`}
                                            value={formData.idAreaNegocio}
                                            onChange={handleChange}
                                        >
                                            <option key="default-area" value="">Selecciona un área...</option>
                                            {areasNegocio.map((a, idx) => (
                                                <option key={a.idAreaNegocio || a.IdAreaNegocio || idx} value={a.idAreaNegocio || a.IdAreaNegocio}>{a.nombre || a.Nombre}</option>
                                            ))}
                                        </select>
                                        {errores.idAreaNegocio && <p className="intra-error-text">{errores.idAreaNegocio}</p>}
                                    </div>
                                </div>

                                <div className="intra-form-group">
                                    <label className="intra-label">Descripción</label>
                                    <textarea
                                        name="descripcion"
                                        className={`intra-textarea ${errores.descripcion ? "intra-input-error" : ""}`}
                                        rows={4}
                                        value={formData.descripcion}
                                        onChange={handleChange}
                                    />
                                    {errores.descripcion && <p className="intra-error-text">{errores.descripcion}</p>}
                                </div>

                                <div className="intra-form-row">
                                    <div className="intra-form-group">
                                        <label className="intra-label">Fecha de fin</label>
                                        <input name="fechaFin" type="date" className="intra-input" value={formData.fechaFin} onKeyDown={(e) => e.preventDefault()} onChange={handleChange} />
                                        {errores.fechaFin && <p className="intra-error-text">{errores.fechaFin}</p>}
                                    </div>
                                </div>

                                <div className="intra-form-group">
                                    <label className="intra-label">Imagen</label>
                                    <div className={`file-uploader${imagePreview ? ' file-uploader--filled' : ''}${errores.imagen ? ' file-uploader--error' : ''}`}>
                                        {imagePreview && (
                                            <div className="file-uploader__badge">
                                                <span>✓</span>
                                                <span>Archivo cargado</span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault(); e.stopPropagation();
                                                        setImagePreview(null); setSelectedFile(null); setFormData(p => ({ ...p, imagen: null }));
                                                    }}
                                                    style={{ marginLeft: '8px', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem' }}
                                                    title="Eliminar imagen"
                                                >×</button>
                                            </div>
                                        )}
                                        {imagePreview && (
                                            <div className="file-uploader__preview">
                                                <img
                                                    src={imagePreview}
                                                    alt="Vista previa"
                                                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px', objectFit: 'contain' }}
                                                />
                                            </div>
                                        )}
                                        <label className="file-uploader__label">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onClick={(e) => { e.target.value = ''; }}
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
                                                        if (file.size > maxMB * 1024 * 1024) {
                                                            setErrores(prev => ({ ...prev, imagen: `La imagen no debe superar los ${maxMB}MB.` }));
                                                            return;
                                                        }
                                                        setErrores(prev => ({ ...prev, imagen: null }));
                                                        setSelectedFile(file);
                                                        setImagePreview(URL.createObjectURL(file));
                                                    }
                                                }}
                                            />
                                            <span className="file-uploader__icon">{imagePreview ? '↻' : '☁'}</span>
                                            <span className="file-uploader__text">{imagePreview ? 'Cambiar imagen' : 'Haz clic para subir imagen'}</span>
                                            <span className="file-uploader__hint">JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)</span>
                                        </label>
                                    </div>
                                    {errores.imagen && <p className="intra-error-text" style={{ marginTop: '0.5rem' }}>{errores.imagen}</p>}
                                </div>

                                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #f1f5f9' }}>
                                    <button
                                        type="submit"
                                        className="btn-action"
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: isUploading ? 0.7 : 1, cursor: isUploading ? 'not-allowed' : 'pointer' }}
                                        disabled={isUploading}
                                    >
                                        <span>{isUploading ? 'Guardando...' : 'Guardar cambios'}</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showShareModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050, padding: '2rem' }}>
                    <div style={{ width: '100%', maxWidth: '500px', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Compartir Aviso</h2>
                            <button type="button" onClick={() => { setShowShareModal(false); setShowModal(true); }} style={{ background: 'none', border: 'none', fontSize: '2rem', color: '#64748b', cursor: 'pointer' }}>×</button>
                        </div>
                        <div style={{ padding: '1.5rem', overflowY: 'auto', maxHeight: '60vh' }}>
                            <p style={{ marginBottom: '1.5rem', color: '#475569' }}>Seleccione las empresas con las que desea compartir este aviso:</p>
                            
                            {descendantCompanies.length === 0 ? (
                                <p style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>No hay empresas hijas disponibles.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {descendantCompanies.map(comp => {
                                        const cId = comp.IdEmpresa || comp.idEmpresa;
                                        const cName = comp.Nombre || comp.nombre;
                                        const cParent = comp.NombrePadre || comp.nombrePadre || 'N/A';
                                        return (
                                            <div key={cId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', backgroundColor: '#f1f5f9', borderRadius: '8px' }}>
                                                <div>
                                                    <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{cName}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>({cParent})</div>
                                                </div>
                                                <div>
                                                    <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={!!selectedCompanies[cId]}
                                                            onChange={(e) => setSelectedCompanies(prev => ({ ...prev, [cId]: e.target.checked }))}
                                                            style={{ opacity: 0, width: 0, height: 0 }}
                                                        />
                                                        <span style={{
                                                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                                            backgroundColor: selectedCompanies[cId] ? '#10b981' : '#cbd5e1',
                                                            transition: '.4s', borderRadius: '24px'
                                                        }}>
                                                            <span style={{
                                                                position: 'absolute', content: '""', height: '18px', width: '18px', left: '3px', bottom: '3px',
                                                                backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                                                transform: selectedCompanies[cId] ? 'translateX(20px)' : 'translateX(0)'
                                                            }} />
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                        <div style={{ padding: '1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                            <button 
                                type="button" 
                                onClick={() => { setShowShareModal(false); setShowModal(true); }}
                                className="intra-button-ghost"
                            >
                                Cancelar
                            </button>
                            <button 
                                type="button" 
                                onClick={() => executePublish(selectedCompanies)}
                                className="btn-action"
                                disabled={sharingLoading}
                                style={{ opacity: sharingLoading ? 0.7 : 1 }}
                            >
                                {sharingLoading ? 'Guardando...' : 'Confirmar y Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* DIALOGS */}
            <AlertDialog
                open={alertDialog.open}
                toOpen={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={alertDialog.onClose}
            />
            <ConfirmDialog
                open={confirmDialog.open}
                toOpen={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                variant={confirmDialog.variant}
                cancelText="Cancelar"
                confirmText={confirmDialog.variant === 'danger' ? 'Eliminar' : 'Confirmar'}
                onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            />

            <ConfirmDeleteDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, item: null })}
                onConfirm={executeDelete}
                roleName={deleteDialog.item?.titulo || "este aviso"}
            />
        </div>
    );
};

export default AvisosUrgentes;
